import { useCallback, useMemo, useRef, useState } from "react";
import { User } from "../../../../../../api/accessControl/User.ts";
import { BackendApi } from "../../../../../../api/api.ts";
import { PendingDraftMatch } from "../DraftMatch.ts";
import { MissingDocumentRequest } from "../MissingDocumentRequest.ts";
import { TransitioningDraft } from "../PendingDraftMatchesSection.tsx";

const refreshDebounceMs = 2000;
const transitionAnimationMs = 2000;

interface UseDraftMatchesOptions {
    currentUser: User;
    canDraftMatchDocuments: boolean;
}

export function useDraftMatches({ currentUser, canDraftMatchDocuments }: UseDraftMatchesOptions) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);

    const [pendingDrafts, setPendingDrafts] = useState<PendingDraftMatch[]>([]);
    const [transitioningDrafts, setTransitioningDrafts] = useState<TransitioningDraft[]>([]);
    const [missingDocuments, setMissingDocuments] = useState<MissingDocumentRequest[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const previousDraftsByIdRef = useRef<Map<string, PendingDraftMatch>>(new Map());
    const animatedTransitionsRef = useRef<Set<string>>(new Set());
    const heldBackTxIdsRef = useRef<Set<string>>(new Set());
    const refreshDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const animationTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

    const mapUnmatched = useCallback(
        (unmatchedResp: unknown): MissingDocumentRequest[] =>
            (unmatchedResp as any[]).map(({ id, ...rest }) => ({ transaction_id: id, ...rest })) as MissingDocumentRequest[],
        []
    );

    const diffDraftSnapshots = useCallback((
        newPendingDrafts: PendingDraftMatch[],
        newUnmatched: MissingDocumentRequest[]
    ) => {
        const newPendingById = new Map(newPendingDrafts.map((draft) => [draft.id, draft]));
        const newUnmatchedTxIds = new Set(newUnmatched.map((tx) => tx.transaction_id));

        const newlyTransitioning: TransitioningDraft[] = [];

        previousDraftsByIdRef.current.forEach((previousDraft, draftId) => {
            if (newPendingById.has(draftId)) return;

            const outcome: TransitioningDraft['outcome'] =
                newUnmatchedTxIds.has(previousDraft.transaction_id) ? 'rejected' : 'resolved';

            const transitionKey = `${draftId}:${outcome}`;
            if (animatedTransitionsRef.current.has(transitionKey)) return;
            animatedTransitionsRef.current.add(transitionKey);

            if (outcome === 'rejected') {
                heldBackTxIdsRef.current.add(previousDraft.transaction_id);
            }

            newlyTransitioning.push({ draft: previousDraft, outcome });
        });

        previousDraftsByIdRef.current = newPendingById;

        if (newlyTransitioning.length > 0) {
            setTransitioningDrafts((current) => [...current, ...newlyTransitioning]);

            newlyTransitioning.forEach(({ draft }) => {
                const timerId = setTimeout(() => {
                    animationTimersRef.current.delete(timerId);
                    setTransitioningDrafts((current) => current.filter((item) => item.draft.id !== draft.id));
                    if (heldBackTxIdsRef.current.delete(draft.transaction_id)) {
                        setMissingDocuments((current) => [...current]);
                    }
                }, transitionAnimationMs);
                animationTimersRef.current.add(timerId);
            });
        }
    }, []);

    const fetchDraftMatchData = useCallback(async () => {
        const [pendingDraftsResp, unmatchedResp] = await Promise.all([
            api.Entrepreneur__getPendingDraftMatches({}),
            api.Entrepreneur__getUnmatchedTransactions({}),
        ]);

        const newPendingDrafts = pendingDraftsResp as PendingDraftMatch[];
        const newUnmatched = mapUnmatched(unmatchedResp);

        diffDraftSnapshots(newPendingDrafts, newUnmatched);

        setPendingDrafts(newPendingDrafts);
        setMissingDocuments(newUnmatched);
    }, [api, diffDraftSnapshots, mapUnmatched]);

    const loadMissingDocumentsOnly = useCallback(async () => {
        const resp = await api.Entrepreneur__getUnmatchedTransactions({});
        setMissingDocuments(mapUnmatched(resp));
    }, [api, mapUnmatched]);

    const loadDraftMatches = useCallback(async () => {
        if (canDraftMatchDocuments) {
            await fetchDraftMatchData();
        } else {
            await loadMissingDocumentsOnly();
        }
    }, [canDraftMatchDocuments, fetchDraftMatchData, loadMissingDocumentsOnly]);

    const refreshDraftMatches = useCallback(() => {
        if (refreshDebounceTimerRef.current) return;

        setIsRefreshing(true);
        fetchDraftMatchData().finally(() => {
            setIsRefreshing(false);
            refreshDebounceTimerRef.current = setTimeout(() => {
                refreshDebounceTimerRef.current = null;
            }, refreshDebounceMs);
        });
    }, [fetchDraftMatchData]);

    const cancelDraftMatch = useCallback(async (draftMatchId: string) => {
        await api.Entrepreneur__cancelDraftMatch({ draft_match_id: draftMatchId });
        previousDraftsByIdRef.current.delete(draftMatchId);
        setPendingDrafts((prev) => prev.filter((draft) => draft.id !== draftMatchId));
        await fetchDraftMatchData();
    }, [api, fetchDraftMatchData]);

    const cleanup = useCallback(() => {
        if (refreshDebounceTimerRef.current) {
            clearTimeout(refreshDebounceTimerRef.current);
            refreshDebounceTimerRef.current = null;
        }
        animationTimersRef.current.forEach((timerId) => clearTimeout(timerId));
        animationTimersRef.current.clear();
        heldBackTxIdsRef.current.clear();
    }, []);

    const derivedMissingDocuments = useMemo(() => {
        if (!canDraftMatchDocuments) return missingDocuments;

        const pendingDraftTxIds = new Set(pendingDrafts.map((draft) => draft.transaction_id));
        return missingDocuments.filter(
            (doc) => !pendingDraftTxIds.has(doc.transaction_id) && !heldBackTxIdsRef.current.has(doc.transaction_id)
        );
    }, [canDraftMatchDocuments, missingDocuments, pendingDrafts]);

    return {
        pendingDrafts,
        transitioningDrafts,
        missingDocuments,
        setMissingDocuments,
        derivedMissingDocuments,
        isRefreshing,
        loadDraftMatches,
        fetchDraftMatchData,
        refreshDraftMatches,
        cancelDraftMatch,
        cleanup,
    };
}
